/**
 * Regions API Types & Schemas
 * Data Transfer Objects (DTOs) for API communication with Strapi
 *
 * Pattern:
 * - API types are SEPARATE from domain types
 * - Use Zod schemas for runtime validation
 * - Export inferred types from schemas
 * - Transformers convert between API ↔ Domain
 */

import { z } from '@spcc/core'

// ===========================================
// RELATED ENTITY SCHEMAS
// ===========================================

/** Country DTO schema */
export const countryDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional().default(true),
})

/** Factory DTO schema
 * Note: When populated through relation, Strapi returns name as simple string.
 * When fetched directly with locale, name is also a string.
 */
export const factoryDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  code: z.string(),
  // Strapi returns name as string when populated through relations
  name: z.string(),
  factoryStatus: z.enum(['active', 'inactive']).optional(),
})

/** City DTO schema */
export const cityDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  isActive: z.boolean().optional().default(true),
})

// ===========================================
// REGION API SCHEMAS
// ===========================================

/**
 * Region DTO Schema - What Strapi API returns
 * Maps to api::region.region schema
 */
export const regionDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  // Relations (optional, populated based on query)
  country: countryDtoSchema.nullable().optional(),
  factories: z.array(factoryDtoSchema).nullable().optional(),
  cities: z.array(cityDtoSchema).nullable().optional(),
  // Metadata
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
})

/**
 * Create Region Request Schema
 */
export const createRegionRequestSchema = z.object({
  code: z.string().min(1, 'Code is required').max(50),
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().optional(),
  isActive: z.boolean().optional().default(true),
  country: z.union([z.string(), z.number()]).optional(), // Country ID
  factories: z.array(z.union([z.string(), z.number()])).optional(), // Factory IDs
  metadata: z.record(z.string(), z.unknown()).optional(),
})

/**
 * Update Region Request Schema
 */
export const updateRegionRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  country: z.union([z.string(), z.number()]).nullable().optional(),
  factories: z.array(z.union([z.string(), z.number()])).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
})

/**
 * Region List Query Schema - Filtering/pagination for Strapi
 */
export const regionListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  isActive: z.boolean().optional(),
  countryId: z.string().optional(),
  sort: z.string().optional().default('name:asc'),
})

/**
 * Strapi Pagination Meta Schema
 */
export const strapiPaginationSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  pageCount: z.number(),
  total: z.number(),
})

/**
 * Region List Response Schema - Strapi paginated response
 */
export const regionListResponseSchema = z.object({
  data: z.array(regionDtoSchema),
  meta: z.object({
    pagination: strapiPaginationSchema,
  }),
})

/**
 * Single Region Response Schema - Strapi single response
 */
export const regionResponseSchema = z.object({
  data: regionDtoSchema,
  meta: z.object({}).optional(),
})

// ===========================================
// INFERRED TYPES (from schemas)
// ===========================================

/** Country DTO type */
export type CountryDto = z.infer<typeof countryDtoSchema>

/** Factory DTO type */
export type FactoryDto = z.infer<typeof factoryDtoSchema>

/** City DTO type */
export type CityDto = z.infer<typeof cityDtoSchema>

/** Region DTO type */
export type RegionDto = z.infer<typeof regionDtoSchema>

/** Create Region request type */
export type CreateRegionApiRequest = z.infer<typeof createRegionRequestSchema>

/** Update Region request type */
export type UpdateRegionApiRequest = z.infer<typeof updateRegionRequestSchema>

/** Region list query params type */
export type RegionListQuery = z.infer<typeof regionListQuerySchema>

/** Region list response type */
export type RegionListResponse = z.infer<typeof regionListResponseSchema>

/** Single Region response type */
export type RegionResponse = z.infer<typeof regionResponseSchema>

/** Strapi pagination type */
export type StrapiPagination = z.infer<typeof strapiPaginationSchema>

// ===========================================
// VALIDATION HELPERS
// ===========================================

/**
 * Validate region DTO from API response
 * @throws ZodError if validation fails
 */
export function validateRegionDto(data: unknown): RegionDto {
  return regionDtoSchema.parse(data)
}

/**
 * Validate region list response from API
 * @throws ZodError if validation fails
 */
export function validateRegionListResponse(data: unknown): RegionListResponse {
  return regionListResponseSchema.parse(data)
}

/**
 * Safe parse - returns success/error instead of throwing
 */
export function safeParseRegionDto(data: unknown) {
  return regionDtoSchema.safeParse(data)
}

export function safeParseRegionList(data: unknown) {
  return regionListResponseSchema.safeParse(data)
}

// ===========================================
// API ENDPOINTS (constants)
// ===========================================

export const REGION_ENDPOINTS = {
  LIST: '/api/regions',
  DETAIL: (documentId: string) => `/api/regions/${documentId}`,
  CREATE: '/api/regions',
  UPDATE: (documentId: string) => `/api/regions/${documentId}`,
  DELETE: (documentId: string) => `/api/regions/${documentId}`,
} as const

export const CITY_ENDPOINTS = {
  LIST: '/api/cities',
} as const

// ===========================================
// POPULATE PARAMS (for Strapi)
// ===========================================

export const REGION_POPULATE = {
  DEFAULT: {
    'populate[country][fields][0]': 'documentId',
    'populate[country][fields][1]': 'name',
    'populate[country][fields][2]': 'code',
    'populate[factories][fields][0]': 'documentId',
    'populate[factories][fields][1]': 'name',
    'populate[factories][fields][2]': 'code',
    'populate[factories][fields][3]': 'factoryStatus',
  },
  WITH_CITIES: {
    'populate[country][fields][0]': 'documentId',
    'populate[country][fields][1]': 'name',
    'populate[country][fields][2]': 'code',
    'populate[factories][fields][0]': 'documentId',
    'populate[factories][fields][1]': 'name',
    'populate[factories][fields][2]': 'code',
    'populate[factories][fields][3]': 'factoryStatus',
    'populate[cities][fields][0]': 'documentId',
    'populate[cities][fields][1]': 'name',
    'populate[cities][fields][2]': 'code',
  },
  MINIMAL: {},
} as const
